"use client";

import { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { CheckIcon, AlertTriangleIcon, InfoIcon } from "lucide-react";

import { 
  ProjectIntakeFormSchema,
  getDefaultFormValues,
  calculateClientSideLeadScore,
  validateBusinessLogic,
  getServiceTrackDisplay,
  getChallengeDisplay,
  getUrgencyDisplay,
  getBudgetDisplay,
  getCompanySizeDisplay,
  calculateFormProgress,
  type ProjectIntakeFormData
} from "@/lib/schemas/project-intake";

import { 
  HOLISTIKA_SERVICE_TRACKS,
  COMPANY_SIZES,
  INDUSTRIES,
  SPECIFIC_CHALLENGES,
  URGENCY_LEVELS,
  BUDGET_RANGES,
  DECISION_MAKER_LEVELS,
  CONTACT_METHODS,
  SENIORITY_LEVELS
} from "@/lib/types/erp";

import { pushLeadIntent } from "@/lib/gtm-data-layer";

import { Button } from "@/components/ui/button";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";

interface ProjectIntakeFormProps {
  initialUtm?: Record<string, string>;
  onSuccess?: (intakeId: string) => void;
  className?: string;
}

export function ProjectIntakeForm({ 
  initialUtm, 
  onSuccess,
  className 
}: ProjectIntakeFormProps) {
  const router = useRouter();
  const { toast } = useToast();
  
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [businessWarnings, setBusinessWarnings] = useState<string[]>([]);
  
  const form = useForm<ProjectIntakeFormData>({
    resolver: zodResolver(ProjectIntakeFormSchema),
    defaultValues: getDefaultFormValues(),
    mode: 'onChange' // Validate on change for better UX
  });
  
  const watchedValues = form.watch();
  
  // Real-time lead score calculation (UX feedback)
  const leadScore = useMemo(() => {
    try {
      return calculateClientSideLeadScore(watchedValues);
    } catch {
      return 0;
    }
  }, [watchedValues]);
  
  // Real-time form progress
  const formProgress = useMemo(() => {
    return calculateFormProgress(watchedValues);
  }, [watchedValues]);
  
  // Business validation warnings (real-time)
  useEffect(() => {
    if (watchedValues.serviceTrack && watchedValues.specificChallenges?.length > 0) {
      const validation = validateBusinessLogic(watchedValues);
      setBusinessWarnings(validation.warnings);
    }
  }, [watchedValues.serviceTrack, watchedValues.specificChallenges]);
  
  // Auto-save to localStorage (UX improvement)
  useEffect(() => {
    const saveTimeout = setTimeout(() => {
      if (formProgress > 10) { // Don't save empty forms
        localStorage.setItem('holistika_intake_draft', JSON.stringify(watchedValues));
      }
    }, 2000); // Debounce saves
    
    return () => clearTimeout(saveTimeout);
  }, [watchedValues, formProgress]);
  
  // Restore draft on mount
  useEffect(() => {
    const savedDraft = localStorage.getItem('holistika_intake_draft');
    if (savedDraft) {
      try {
        const draftData = JSON.parse(savedDraft);
        // Only restore if form is empty
        if (formProgress < 10) {
          Object.entries(draftData).forEach(([key, value]) => {
            if (value && key in getDefaultFormValues()) {
              form.setValue(key as keyof ProjectIntakeFormData, value as any);
            }
          });
          
          toast({
            title: "Draft restored",
            description: "We've restored your previous draft.",
            variant: "default"
          });
        }
      } catch {
        localStorage.removeItem('holistika_intake_draft');
      }
    }
  }, []);
  
  async function onSubmit(formData: ProjectIntakeFormData) {
    setStatus('submitting');
    setErrorMessage(null);
    
    try {
      // Capture session context for attribution
      const sessionMetadata = {
        pageUrl: window.location.href,
        referrer: document.referrer,
        userAgent: navigator.userAgent,
        ga4ClientId: getGA4ClientId(),
        utm: initialUtm,
        browserInfo: {
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          screenResolution: `${screen.width}x${screen.height}`
        },
        timestamp: new Date().toISOString()
      };
      
      const response = await fetch('/api/project-intake', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formData,
          sessionMetadata
        })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        if (result.fieldErrors) {
          // Handle validation errors
          Object.entries(result.fieldErrors).forEach(([field, errors]) => {
            form.setError(field as keyof ProjectIntakeFormData, {
              message: Array.isArray(errors) ? errors[0] : errors
            });
          });
          setStatus('idle');
          return;
        }
        
        throw new Error(result.error || 'Submission failed');
      }
      
      setStatus('success');
      
      // Clear draft
      localStorage.removeItem('holistika_intake_draft');
      
      // Show success message
      toast({
        title: "Project brief submitted!",
        description: result.message || "We'll be in touch soon.",
        variant: "default"
      });
      
      // Measurement — fire ONLY after server success.
      // Generate a shared dedupe id so dataLayer + gtag (+ any future server-side
      // Meta/GA Conversions API send) can be matched by `event_id`.
      const dedupeId =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `lead_${Date.now()}`;

      // 1) Structured dataLayer push for GTM (no PII).
      pushLeadIntent({ formType: "project_intake", eventId: dedupeId });

      // 2) Direct gtag event (kept for backwards compatibility with existing tags).
      if (window.gtag) {
        window.gtag('event', 'lead_intent', {
          cta_text: 'Send project brief',
          cta_location: 'make_a_project_form',
          destination: '/make-a-project',
          lead_score: result.data.leadScore,
          service_track: formData.serviceTrack,
          budget_range: formData.budgetRange,
          company_size: formData.companySize,
          value: result.data.leadScore,
          event_id: dedupeId,
        });
      }
      
      // Callback or redirect
      if (onSuccess) {
        onSuccess(result.data.intakeId);
      } else {
        router.push(`/make-a-project/success?intake_id=${result.data.intakeId}&score=${result.data.leadScore}`);
      }
      
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again.');
      
      toast({
        title: "Submission failed",
        description: "Please check your information and try again.",
        variant: "destructive"
      });
    }
  }
  
  // Success state
  if (status === 'success') {
    return <ProjectIntakeSuccessCard />;
  }
  
  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl">Let's understand your project</CardTitle>
            <CardDescription>
              We'll match you to the right Holistika track and respond within 24 hours.
            </CardDescription>
          </div>
          
          {/* Form progress indicator */}
          <div className="text-right space-y-2">
            <div className="text-sm text-muted-foreground">Progress</div>
            <Progress value={formProgress} className="w-24" />
            <div className="text-xs text-muted-foreground">{formProgress}%</div>
          </div>
        </div>
        
        {/* Lead score indicator (real-time feedback) */}
        {leadScore > 0 && (
          <div className="flex items-center gap-2 mt-4 p-3 bg-muted rounded-lg">
            <InfoIcon className="h-4 w-4 text-blue-500" />
            <span className="text-sm">
              <strong>Match Score: {leadScore}/100</strong>
              {leadScore >= 70 && " • High priority - we'll respond within 4 hours"}
              {leadScore >= 40 && leadScore < 70 && " • Good fit - expect response within 24 hours"}
              {leadScore < 40 && " • We'll review and respond within 48 hours"}
            </span>
          </div>
        )}
        
        {/* Business logic warnings */}
        {businessWarnings.length > 0 && (
          <div className="space-y-2 mt-4">
            {businessWarnings.map((warning, index) => (
              <div key={index} className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertTriangleIcon className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-yellow-800">{warning}</p>
              </div>
            ))}
          </div>
        )}
      </CardHeader>
      
      <CardContent>
        {/* General error message */}
        {status === 'error' && errorMessage && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/40 rounded-lg">
            <p className="text-sm text-destructive font-medium">{errorMessage}</p>
          </div>
        )}
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            
            {/* ===== CONTACT INFORMATION ===== */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold">Contact Information</h3>
                <Badge variant="outline">Required</Badge>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Your first name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Your last name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Work Email</FormLabel>
                      <FormControl>
                        <Input 
                          type="email" 
                          placeholder="name@company.com" 
                          {...field} 
                        />
                      </FormControl>
                      <FormDescription>
                        We'll use this to send you project updates
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="companyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Your company" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="jobTitle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Job Title</FormLabel>
                      <FormControl>
                        <Input placeholder="Your role" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone (optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="+1 (555) 123-4567" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="website"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Company Website (optional)</FormLabel>
                      <FormControl>
                        <Input 
                          type="url" 
                          placeholder="https://yourcompany.com" 
                          {...field} 
                        />
                      </FormControl>
                      <FormDescription>
                        Helps us understand your business better
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            
            <Separator />
            
            {/* ===== COMPANY CONTEXT ===== */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Company Context</h3>
              
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="companySize"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Size</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select company size" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {COMPANY_SIZES.map((size) => (
                            <SelectItem key={size} value={size}>
                              {getCompanySizeDisplay(size)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="industry"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Industry</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select industry" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="technology">Technology & Software</SelectItem>
                          <SelectItem value="finance_banking">Finance & Banking</SelectItem>
                          <SelectItem value="healthcare">Healthcare & Life Sciences</SelectItem>
                          <SelectItem value="manufacturing">Manufacturing</SelectItem>
                          <SelectItem value="retail_ecommerce">Retail & E-commerce</SelectItem>
                          <SelectItem value="consulting_services">Consulting & Services</SelectItem>
                          <SelectItem value="energy_utilities">Energy & Utilities</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            
            <Separator />
            
            {/* ===== HOLISTIKA SERVICE MATCHING ===== */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold">Which Holistika track fits your needs?</h3>
                <Badge variant="outline">Helps us route to the right team</Badge>
              </div>
              
              <FormField
                control={form.control}
                name="serviceTrack"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="grid gap-3">
                        {HOLISTIKA_SERVICE_TRACKS.map((track) => {
                          const display = getServiceTrackDisplay(track);
                          return (
                            <label
                              key={track}
                              className={`flex items-start space-x-3 p-4 rounded-lg border cursor-pointer transition-colors hover:bg-muted ${
                                field.value === track 
                                  ? 'border-primary bg-primary/5' 
                                  : 'border-border'
                              }`}
                            >
                              <input
                                type="radio"
                                value={track}
                                checked={field.value === track}
                                onChange={() => field.onChange(track)}
                                className="mt-1"
                              />
                              <div className="flex-1">
                                <div className="font-medium">{display.label}</div>
                                <div className="text-sm text-muted-foreground mt-1">
                                  {display.description}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="urgency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Timeline</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="When do you want to start?" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {URGENCY_LEVELS.map((urgency) => {
                            const display = getUrgencyDisplay(urgency);
                            return (
                              <SelectItem key={urgency} value={urgency}>
                                {display.label}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="budgetRange"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Budget Range</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select budget range" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {BUDGET_RANGES.map((budget) => (
                            <SelectItem key={budget} value={budget}>
                              {getBudgetDisplay(budget)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Helps us recommend the right engagement model
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            
            <Separator />
            
            {/* ===== BUSINESS INTELLIGENCE ===== */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold">Tell us about your situation</h3>
                <Badge variant="outline">Feeds our Foresight process</Badge>
              </div>
              
              <FormField
                control={form.control}
                name="currentSituation"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>What's the current situation?</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Describe what's happening, what's blocked, what you've tried so far..."
                        className="min-h-[120px]" 
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Example: "We're a 50-person SaaS startup with product-market fit, but our go-to-market strategy is fragmented. We've tried different approaches but lack a systematic framework."
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="specificChallenges"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Specific challenges (select all that apply)</FormLabel>
                    <FormDescription>
                      This helps us understand which Holistika track(s) would be most valuable
                    </FormDescription>
                    <FormControl>
                      <div className="grid gap-2 md:grid-cols-2">
                        {SPECIFIC_CHALLENGES.map((challenge) => (
                          <FormItem
                            key={challenge}
                            className="flex items-center space-x-2 space-y-0"
                          >
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(challenge)}
                                onCheckedChange={(checked) => {
                                  const current = field.value || [];
                                  if (checked) {
                                    field.onChange([...current, challenge]);
                                  } else {
                                    field.onChange(current.filter(c => c !== challenge));
                                  }
                                }}
                              />
                            </FormControl>
                            <FormLabel className="text-sm font-normal leading-none">
                              {getChallengeDisplay(challenge)}
                            </FormLabel>
                          </FormItem>
                        ))}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="desiredOutcome"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>What does success look like? (optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Describe your ideal outcome..."
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Help us understand how to measure project success
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <Separator />
            
            {/* ===== DECISION-MAKING CONTEXT ===== */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Decision-making context</h3>
              
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="decisionMaker"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Your role in this decision</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select your role" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="yes_iam">I make the final decision</SelectItem>
                          <SelectItem value="yes_with_approval">I decide, but need approval</SelectItem>
                          <SelectItem value="no_but_influencer">I'm an influencer/evaluator</SelectItem>
                          <SelectItem value="no_just_research">Just researching options</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="decisionTimeline"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Decision timeline</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="When will you decide?" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="deciding_now">Making decision now</SelectItem>
                          <SelectItem value="within_month">Within a month</SelectItem>
                          <SelectItem value="next_quarter">Next quarter</SelectItem>
                          <SelectItem value="just_exploring">Just exploring</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <FormField
                control={form.control}
                name="stakeholdersInvolved"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Other stakeholders involved (optional)</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="e.g., CEO, CTO, Operations Director..."
                        {...field} 
                      />
                    </FormControl>
                    <FormDescription>
                      Helps us understand the decision-making process
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="previousConsultancy"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>We've worked with consultants before</FormLabel>
                      <FormDescription>
                        Helps us understand your experience with consulting engagements
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            </div>
            
            <Separator />
            
            {/* ===== COMMUNICATION PREFERENCES ===== */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Communication preferences</h3>
              
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="preferredContactMethod"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preferred contact method</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="phone">Phone call</SelectItem>
                          <SelectItem value="video_call">Video call</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="bestTimeToContact"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Best time to contact</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="When works best?" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="morning_9_12">Morning (9 AM - 12 PM)</SelectItem>
                          <SelectItem value="afternoon_12_17">Afternoon (12 PM - 5 PM)</SelectItem>
                          <SelectItem value="evening_17_20">Evening (5 PM - 8 PM)</SelectItem>
                          <SelectItem value="flexible">Flexible</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            
            <Separator />
            
            {/* ===== CONSENT & PRIVACY ===== */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Privacy & consent</h3>
              
              <div className="space-y-3">
                <FormField
                  control={form.control}
                  name="consentContact"
                  render={({ field }) => (
                    <FormItem className="flex items-start space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-sm font-medium">
                          Yes, Holistika can contact me about this project inquiry
                        </FormLabel>
                        <FormDescription>
                          Required to process your request and schedule discovery calls
                        </FormDescription>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="consentMarketing"
                  render={({ field }) => (
                    <FormItem className="flex items-start space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-sm font-medium">
                          Send me Holistika insights and case studies (optional)
                        </FormLabel>
                        <FormDescription>
                          Occasional emails about methodology insights and success stories
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="consentDataProcessing" 
                  render={({ field }) => (
                    <FormItem className="flex items-start space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-sm font-medium">
                          I consent to data processing as described in the privacy policy
                        </FormLabel>
                        <FormDescription>
                          Required for GDPR compliance
                        </FormDescription>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />
              </div>
            </div>
            
            {/* Anti-spam honeypot (hidden) */}
            <FormField
              control={form.control}
              name="honeypot"
              render={({ field }) => (
                <FormItem className="hidden">
                  <FormControl>
                    <Input {...field} tabIndex={-1} autoComplete="off" />
                  </FormControl>
                </FormItem>
              )}
            />
            
            {/* ===== SUBMIT ===== */}
            <div className="space-y-4">
              <Button 
                type="submit" 
                disabled={status === 'submitting' || formProgress < 70}
                className="w-full h-12"
                size="lg"
              >
                {status === 'submitting' ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Processing your brief...
                  </>
                ) : (
                  'Send project brief'
                )}
              </Button>
              
              {formProgress < 70 && (
                <p className="text-sm text-muted-foreground text-center">
                  Please complete the required fields above ({Math.round(formProgress)}% complete)
                </p>
              )}
              
              <p className="text-xs text-muted-foreground text-center">
                By submitting, you agree to our terms of service and privacy policy.
                We'll use this information to understand your needs and provide relevant project proposals.
              </p>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}

// ============================================
// SUCCESS STATE COMPONENT
// ============================================

function ProjectIntakeSuccessCard() {
  return (
    <Card className="border-green-200 bg-green-50/50">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <CheckIcon className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <CardTitle className="text-green-900">Project brief received!</CardTitle>
            <CardDescription className="text-green-700">
              We're reviewing your submission and will respond soon.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="bg-white/60 rounded-lg p-4">
          <h4 className="font-medium text-green-900 mb-2">What happens next:</h4>
          <ol className="list-decimal list-inside space-y-1 text-sm text-green-800">
            <li>We review your brief and match you to the right Holistika team</li>
            <li>You'll get a discovery call invite within 24-48 hours</li>
            <li>We provide a scoped proposal after understanding your needs</li>
            <li>If aligned, we begin with our proven 3-phase methodology</li>
          </ol>
        </div>
        
        <div className="flex gap-3">
          <Button variant="outline" asChild className="flex-1">
            <a href="/think-big">Explore Our Services</a>
          </Button>
          <Button asChild className="flex-1">
            <a href="/">Back to Home</a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

function getGA4ClientId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  
  try {
    // Try to get GA4 client ID from gtag
    if (window.gtag && window.gtag.getAll) {
      const trackers = window.gtag.getAll();
      if (trackers?.[0]?.get) {
        return trackers[0].get('client_id');
      }
    }
    
    // Fallback: try to extract from GA cookies
    const gaCookies = document.cookie
      .split('; ')
      .find(row => row.startsWith('_ga='));
      
    if (gaCookies) {
      const parts = gaCookies.split('.');
      if (parts.length >= 4) {
        return `${parts[2]}.${parts[3]}`;
      }
    }
  } catch (error) {
    console.warn('Could not get GA4 client ID:', error);
  }
  
  return undefined;
}

// ============================================
// TYPE EXTENSIONS (for gtag)
// ============================================

// Canonical Window.gtag declaration lives in types/global.d.ts
// (centralised to avoid declaration-merging conflicts — see I66 P5
// build-fix incident 2026-05-09).
